import { useState } from "react";
import { Images, Image as ImagePlaceholder, Loader2, CheckCircle2, Eye } from "lucide-react";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/Modal";
import toast from "react-hot-toast";

import {
  BRANDING_SHELL,
  BRANDING_SHELL_STYLE,
  BRANDING_CARD_PADDING,
  BRANDING_TILE_RADIUS,
} from "../branding.tokens";
import { BRANDING_ASSET_KINDS, BRANDING_ASSET_META } from "../constants";
import BrandingSectionHeader from "./BrandingSectionHeader";

// One registry for the asset kinds — constants.js — read here instead of
// restated, so this row's title/description can't drift from it.
const ASSET_DEFINITIONS = Object.values(BRANDING_ASSET_KINDS).map((key) => ({
  key,
  title: BRANDING_ASSET_META[key]?.label || key,
  desc: BRANDING_ASSET_META[key]?.helperText || "",
}));

// Mirrors the backend's per-kind limits (server/app/api/v2/branding/service.py
// ASSET_LIMITS) so obviously-invalid files are rejected before the round trip,
// with a clear message instead of a generic server error toast. maxWidth/
// maxHeight are shown here for the user's benefit only — the file docstring
// below on validateAssetFile explains why they aren't checked client-side.
const ASSET_UPLOAD_LIMITS = {
  logo: {
    maxBytes: 500 * 1024, maxWidth: 800, maxHeight: 200,
    mimeTypes: ["image/png", "image/jpeg"], extensions: ["png", "jpg", "jpeg"],
    label: "PNG or JPEG, 500 KB max, 800×200px max",
  },
  logo_dark: {
    maxBytes: 500 * 1024, maxWidth: 800, maxHeight: 200,
    mimeTypes: ["image/png", "image/jpeg"], extensions: ["png", "jpg", "jpeg"],
    label: "PNG or JPEG, 500 KB max, 800×200px max",
  },
  favicon: {
    maxBytes: 100 * 1024, maxWidth: 128, maxHeight: 128,
    mimeTypes: ["image/png", "image/x-icon", "image/vnd.microsoft.icon"], extensions: ["png", "ico"],
    label: "PNG or ICO, 100 KB max, 128×128px max",
  },
  email_logo: {
    maxBytes: 200 * 1024, maxWidth: 400, maxHeight: 100,
    mimeTypes: ["image/png"], extensions: ["png"],
    label: "PNG only, 200 KB max, 400×100px max",
  },
};

const formatKb = (bytes) => `${Math.round(bytes / 1024)} KB`;

// Basic client-side guard, not a replacement for the backend's real
// signature/dimension checks — just catches the obviously-wrong file (wrong
// type or too large) before spending a round trip on it. Falls back to
// checking the file extension because some browsers report an empty or
// inconsistent MIME type for .ico files.
const validateAssetFile = (assetKind, file) => {
  const limit = ASSET_UPLOAD_LIMITS[assetKind];
  if (!limit) return null;

  const ext = file.name?.split(".").pop()?.toLowerCase();
  const typeOk = limit.mimeTypes.includes(file.type) || limit.extensions.includes(ext);
  if (!typeOk) return `Unsupported file. Allowed: ${limit.label}.`;

  if (file.size > limit.maxBytes) {
    return `File is ${formatKb(file.size)} — must be ${formatKb(limit.maxBytes)} or smaller.`;
  }

  return null;
};

const previewableKinds = { logo: "wide", logo_dark: "wide", favicon: "square", email_logo: "wide" };

const thumbSize = (shape) => (shape === "square" ? { width: 40, height: 40 } : { width: 56, height: 36 });

// One labeled thumbnail — an uploaded image, or (when `present` is false but
// the sibling state has an asset) a dashed placeholder that reads "nothing
// here yet" instead of leaving the slot blank. `tone` colors the label so
// draft-only and live-only read apart from a glance. Mirrors
// BrandingPublishedPanel's PublishedAssetThumb: hover reveals an eye icon,
// click opens the same enlarged-preview dialog.
const AssetThumb = ({ url, kind, label, tone, onPreview }) => {
  const shape = previewableKinds[kind] || "wide";
  const size = thumbSize(shape);
  const toneClass =
    tone === "live" ? "text-emerald-600" : tone === "draft" ? "text-amber-600" : "text-text-secondary";

  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className={`group relative flex shrink-0 items-center justify-center overflow-hidden ${BRANDING_TILE_RADIUS} ${
          url ? "cursor-pointer border border-border bg-background-secondary/50" : "border border-dashed border-border bg-background-secondary/20"
        }`}
        style={size}
        onClick={url ? () => onPreview(kind, url, label) : undefined}
        role={url ? "button" : undefined}
        aria-label={url ? `Preview ${label} ${kind}` : undefined}
      >
        {url ? (
          <>
            <img
              src={url}
              alt={`${label} ${kind} preview`}
              className="max-h-full max-w-full object-contain"
              style={{ maxHeight: shape === "square" ? "32px" : "26px" }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
            <div className="absolute inset-0 hidden items-center justify-center bg-background-primary/60 group-hover:flex">
              <Eye className="h-3.5 w-3.5 text-text-primary" aria-hidden="true" />
            </div>
          </>
        ) : (
          <ImagePlaceholder className="h-3.5 w-3.5 text-text-secondary/60" aria-hidden="true" />
        )}
      </div>
      <span className={`text-[9px] font-medium uppercase tracking-wide ${toneClass}`}>{label}</span>
    </div>
  );
};

// The empty state: nothing uploaded for this asset kind at all, on draft or
// live. A plain, non-interactive placeholder — not a button — because the
// row's "Upload" button on the right is the one control that opens the file
// picker; a second clickable target here would just be the same action
// twice, the way it briefly was.
const EmptyAssetPlaceholder = () => (
  <div
    className={`flex h-[56px] w-[72px] shrink-0 flex-col items-center justify-center gap-0.5 ${BRANDING_TILE_RADIUS} border border-dashed border-border bg-background-secondary/20 text-text-secondary/60`}
  >
    <ImagePlaceholder className="h-4 w-4" aria-hidden="true" />
    <span className="text-[9px] font-medium uppercase tracking-wide">Empty</span>
  </div>
);

const AssetRow = ({
  title,
  desc,
  assetKind,
  draftAsset,
  publishedAsset,
  disabled,
  assetLoadingKey,
  isUploading,
  isRemovingDraft,
  isRemovingPublished,
  onUpload,
  onRemoveDraft,
  onRemovePublished,
  onPreview,
}) => {
  const fileInputId = `branding-asset-upload-${assetKind}`;

  // Disable every row's controls while ANY asset operation is in flight
  // (upload/remove on this row or any other) — asset mutations aren't safe
  // to run concurrently — but only the row/button actually doing the work
  // shows a busy label, so the user can see what's happening.
  const anyAssetBusy = Boolean(assetLoadingKey);
  const busy = isUploading || isRemovingDraft || isRemovingPublished;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const validationError = validateAssetFile(assetKind, file);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    onUpload(file);
  };

  const openFilePicker = () => document.getElementById(fileInputId)?.click();

  const hasDraftUrl = Boolean(draftAsset?.url);
  const hasPublishedUrl = Boolean(publishedAsset?.url);
  const hasAnyAsset = hasDraftUrl || hasPublishedUrl;
  const inSync = hasDraftUrl && hasPublishedUrl && draftAsset.url === publishedAsset.url;

  return (
    <div
      className={`flex flex-col gap-3 border ${BRANDING_TILE_RADIUS} bg-background-secondary/30 px-4 py-3 sm:flex-row sm:items-center sm:justify-between`}
      style={BRANDING_SHELL_STYLE}
    >
      <div className="flex items-center gap-3 min-w-0">
        {!hasAnyAsset ? (
          <EmptyAssetPlaceholder />
        ) : hasDraftUrl && hasPublishedUrl && !inSync ? (
          // The only case worth two thumbnails: draft and live both exist and
          // actually differ, so seeing them side by side is the point.
          <div className="flex gap-1.5">
            <AssetThumb url={draftAsset.url} kind={assetKind} label="Draft" tone="draft" onPreview={onPreview} />
            <AssetThumb url={publishedAsset.url} kind={assetKind} label="Live" tone="live" onPreview={onPreview} />
          </div>
        ) : hasPublishedUrl ? (
          <AssetThumb url={publishedAsset.url} kind={assetKind} label="Live" tone="live" onPreview={onPreview} />
        ) : (
          <AssetThumb url={draftAsset.url} kind={assetKind} label="Draft" tone="draft" onPreview={onPreview} />
        )}

        <div className="min-w-0">
          <h4 className="flex items-center gap-1.5 text-[13px] font-semibold text-text-primary">
            {title}
            {hasAnyAsset && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-600">
                <CheckCircle2 className="h-3 w-3" aria-hidden="true" /> Uploaded
              </span>
            )}
          </h4>
          <p className="text-[11px] text-text-secondary">{desc}</p>
          <p className="text-[10px] text-text-secondary mt-0.5">{ASSET_UPLOAD_LIMITS[assetKind]?.label}</p>
          {hasDraftUrl && !hasPublishedUrl && (
            <p className="text-[10px] text-amber-600 mt-0.5">Draft only — not live yet. Publish to make it live.</p>
          )}
          {hasDraftUrl && hasPublishedUrl && !inSync && (
            <p className="text-[10px] text-amber-600 mt-0.5">Draft differs from live. Publish to update it.</p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 shrink-0">
        <Input
          id={fileInputId}
          type="file"
          accept={ASSET_UPLOAD_LIMITS[assetKind]?.mimeTypes?.join(",") || "image/*"}
          className="hidden"
          onChange={handleFileChange}
        />

        {/* The row's one upload control — the thumbnail/placeholder on the
            left is read-only. `size="sm"` rounds itself via `var(--radius-md)`,
            a custom property this app never defines — the arbitrary value
            goes invalid and the button renders with square corners.
            `rounded-lg` overrides it back to the working token on every
            button below (tailwind-merge keeps the last radius class),
            without touching the shared Button.jsx. */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || busy || anyAssetBusy}
          onClick={openFilePicker}
          className="min-w-[84px] rounded-lg"
        >
          {isUploading && <Loader2 size={12} className="animate-spin" />}
          {isUploading ? "Uploading..." : hasAnyAsset ? "Replace" : "Upload"}
        </Button>

        {/* Shown only once there's something to remove — a disabled red
            button next to every never-uploaded asset read as an error the
            page was stuck in, not an inert control. */}
        {draftAsset?.url && (
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={disabled || busy || anyAssetBusy}
            onClick={onRemoveDraft}
            className="rounded-lg"
          >
            {isRemovingDraft && <Loader2 size={12} className="animate-spin" />}
            {isRemovingDraft ? "Removing..." : "Remove Draft"}
          </Button>
        )}

        {publishedAsset?.url && (
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={disabled || busy || anyAssetBusy}
            onClick={onRemovePublished}
            className="rounded-lg"
          >
            {isRemovingPublished && <Loader2 size={12} className="animate-spin" />}
            {isRemovingPublished ? "Removing..." : "Remove Live"}
          </Button>
        )}
      </div>
    </div>
  );
};

const BrandingAssetUploads = ({
  draftAssets,
  publishedAssets,
  disabled,
  assetLoadingKey,
  onUpload,
  onRemoveDraft,
  onRemovePublished,
}) => {
  const [preview, setPreview] = useState(null);

  return (
    <div className={`${BRANDING_SHELL} ${BRANDING_CARD_PADDING}`} style={BRANDING_SHELL_STYLE}>
      <BrandingSectionHeader
        icon={Images}
        title="Assets"
        subtitle="Upload tenant assets for header, browser tab, dark mode, and email branding."
      />

      <div className="space-y-2">
        {ASSET_DEFINITIONS.map((def) => (
          <AssetRow
            key={def.key}
            assetKind={def.key}
            title={def.title}
            desc={def.desc}
            draftAsset={draftAssets?.[def.key]}
            publishedAsset={publishedAssets?.[def.key]}
            disabled={disabled}
            isUploading={assetLoadingKey === `upload-${def.key}`}
            isRemovingDraft={assetLoadingKey === `remove-draft-${def.key}`}
            isRemovingPublished={assetLoadingKey === `remove-published-${def.key}`}
            assetLoadingKey={assetLoadingKey}
            onUpload={(file) => onUpload(def.key, file)}
            onRemoveDraft={() => onRemoveDraft(def.key)}
            onRemovePublished={() => onRemovePublished(def.key)}
            onPreview={(kind, url, label) => setPreview({ kind, url, label })}
          />
        ))}
      </div>

      <Dialog open={!!preview} onOpenChange={(open) => !open && setPreview(null)}>
        <DialogContent className="max-w-lg" showCloseButton>
          <DialogHeader>
            <DialogTitle>
              {preview ? `${BRANDING_ASSET_META[preview.kind]?.label || preview.kind} — ${preview.label}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-6">
            {preview && (
              <img
                src={preview.url}
                alt={`${BRANDING_ASSET_META[preview.kind]?.label || preview.kind} preview`}
                className="max-h-[60vh] max-w-full object-contain"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrandingAssetUploads;
